import { Injectable, Optional } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {MatDialog} from '@angular/material/dialog';
import { MessageService } from './message.service';
import { LoginSession } from './LoginSession';
import { Configurations } from '../../../Configuration';

@Injectable({
	providedIn: 'root'
})
export class LoginService {
	endPoint: string;
	statusObserver = new Subject<any>();

	timer: any;
	config: Configurations;

	constructor(
		private message: MessageService,
		private router: Router,
		private translateService: TranslateService,
		private dialogRef: MatDialog,
		@Optional() config?: Configurations
	) {
		this.config = config;
		this.endPoint = config.getLoginEndPoint() + 'session';

		router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
				this.extendTimeout();
			}
		});	
	}

	hasRole(role: string): boolean {
		const session = this.loadLoginSession();

		if (session && session.roles) {
			for (const userRole of session.roles) {
				if (userRole.role === role) {
					return true;
				}	
			}
		} else if (!this.config.getProduction()) {
			return true;
		} else {
			return false;
		}
	}

	isGroupMember(userId: string): boolean {
		const session = this.loadLoginSession();

		if (session && session.assoicatedGroups) {
			for (const assoicatedGroup of session.assoicatedGroups) {
				if (userId === assoicatedGroup.group.ownerId) {
					return true;
				}
			}
		}
		return false;
	}

	login(username: string, password: string, locale?: string, oauth?: boolean): Observable<any> {
		this.statusObserver = new Subject<any>();
		let mapping = '';
		if (oauth) {
			mapping = '/oauthLogin';
		} else {
			mapping = '/login';
		}
		this.message.postResponse(this.endPoint + mapping, { userId: username, password: password })
		.subscribe(res => {
			if (res && res.status < 400) {
				const user = res.body;
				const loginSession = this.loadLoginSession();

				locale = (user.locale ? user.locale : locale);
				if (!loginSession || loginSession.loginId !== user.userId) {
					this.saveLoginSession({
						'loggedIn': true,
						'loginId': user.userId,
						'loginName': user.username,
						'locale': locale,
						'roles': user.roles,
						'assoicatedGroups': user.associatedGroups,
						'hostGroupId' : user.hostGroupId,
						'accessKey': res.headers.get('AccessKey'),
						'secretKey': res.headers.get('SecretKey'),
						'config': (user.setting ? JSON.parse(user.setting.setting) : {})
					});	
				} else {
					loginSession.loggedIn = true;
					loginSession.loginName = user.username;
					loginSession.locale = locale;
					loginSession.roles = user.roles;
					loginSession.assoicatedGroups = user.assoicatedGroups;
					if (user.setting) {
						loginSession.config = JSON.parse(user.setting.setting);
					}
					loginSession.accessKey = res.headers.get('AccessKey');
					loginSession.secretKey = res.headers.get('SecretKey');

					this.saveLoginSession(loginSession);
				}
				this.translateService.use(locale);
				this.checkTimeout();
				this.statusObserver.next({
					status: 'login',
					loginId: username
				});	
			}
		}, error => {
			this.statusObserver.error({
				status: "error"
			});
		});
		return this.statusObserver;
	}

	validateUserId(userId: string) {
		return this.message.query(this.config.getLoginEndPoint() + 'user/idCheck', { userId: userId });
	}
	
	logout(ignoreLogout = false, removeSession?: boolean) {
		this.dialogRef.closeAll();

		if(localStorage.getItem('locale')) {
			localStorage.removeItem('locale');
		}

		this.statusObserver.next({
			status: "logout"
		});
		
		const loginSession = this.loadLoginSession();

		if (loginSession && loginSession.loggedIn) {
			if (!ignoreLogout) {
				this.message.put(this.endPoint + '/logout', JSON.stringify(loginSession.config))
				.subscribe(() => {
					loginSession.loggedIn = false;
					this.saveLoginSession(loginSession);

					if (removeSession && localStorage.getItem(LoginSession.SessionKey)) {
						localStorage.removeItem(LoginSession.SessionKey);
					}
				}, () => {
					loginSession.loggedIn = false;
					this.saveLoginSession(loginSession);

					if (removeSession && localStorage.getItem(LoginSession.SessionKey)) {
						localStorage.removeItem(LoginSession.SessionKey);
					}
					console.log('Already logged out or not logged in yet !!!');
				});
			} else {
				loginSession.loggedIn = false;
				this.saveLoginSession(loginSession);
				
				if (removeSession && localStorage.getItem(LoginSession.SessionKey)) {
					localStorage.removeItem(LoginSession.SessionKey);
				}
			}
		}
		
		if(this.timer) {
			clearTimeout(this.timer);
		}

	}

	checkTimeout() {
		this.message.patch(this.endPoint + '/checkTimeout', null, null, null, true).subscribe(() => {
			this.timer = setTimeout((self: LoginService) => {
				self.checkTimeout();
			}, 60000, this)
		}, () => {
			this.router.navigate(['/member/logout'], { queryParams: {returnUrl: this.router.url} });
		})
	}

	extendTimeout() {
		if (localStorage.getItem(LoginSession.SessionKey)) {
			this.message.patch(this.endPoint + '/setAccessTime', null, null, null, true).subscribe(() => {
				console.log('Access time updated !!!');
			}, () => {
				console.log('Failed to access timeout !!!');
			});
		} else {
			console.log('No session !!!')
		}
	}

	loadLoginSession(): any {
		const session = localStorage.getItem(LoginSession.SessionKey);

		if (!session) {
			return null;
		} else {
			const parsedSession = JSON.parse(session);

			if (this.config.testUserId && !this.config.getProduction()) {
				if (parsedSession != null) {
					parsedSession.loggedIn = true;
					return parsedSession;
				} else {
					return null;
				}	
			} else {
				if (parsedSession != null && parsedSession.loggedIn) {
					return parsedSession;
				} else {
					return null;
				}	
			}
		}
	}

	saveLoginSession(session: any) {
		localStorage.setItem(LoginSession.SessionKey, JSON.stringify(session));
	}

	getLoginId(): string {
		if (this.config.testUserId && !this.config.getProduction()) {
			return this.config.testUserId;
		} else {
			const session = this.loadLoginSession();

			if (!session) {
				return null;
			} else {
				return session.loginId;
			}
		}
	}

	getLocale(): string {
		const session = this.loadLoginSession();

		if (!session) {
			return null;
		} else {
			return session.locale;
		}
	}

	getLoginName(): string {
		const session = this.loadLoginSession();

		if (!session) {
			return null;
		} else {
			return session.loginName;
		}
	}

	setScreenConfig(screenId: string, property: string, configData: any) {
		const session = this.loadLoginSession();

		if (!session) {
			return;
		}

		const config = (session.config ? session.config : session.config = {});

		if (!config[screenId]) {
			config[screenId] = {};
		}

		const screen = config[screenId];

		if (!screen[property]) {
			screen[property] = {};
		}

		screen[property] = configData;

		this.saveLoginSession(session);
	}

	getScreenConfig(screenId: string, property: string, defaultValue?: any): any {
		const session = this.loadLoginSession();

		if (!session) {
			return (defaultValue ? defaultValue : null);
		} else {
			if (session.config) {
				if (session.config[screenId]) {
					const value = session.config[screenId][property];

					if (!value) {
						return defaultValue;
					} else {
						return value;
					}
				}
			}

			return (defaultValue ? defaultValue : null);
		}
	}
}
